import { createElement, useEffect, useRef } from 'react';
import { mergeDeep } from '@sitecore-search/common';
import { Actions as CoreActions, WidgetsConfig, getDefaultRequestForWidget, propStateMapper, } from '@sitecore-search/core';
import { defaultWidgetConfig } from '@sitecore-search/widgets';
import WidgetContext from '../WidgetContext.js';
import useBaseWidget from '../hooks/useBaseWidget.js';
import { Query } from '../query.js';
const callActionProp = (dispatch, actionType, payload) => {
    dispatch({ type: actionType, payload });
};
const mapActionsProps = (dispatch, options) => {
    const { actionProps = {} } = options;
    return Object.keys(actionProps).reduce((mem, actionProp) => ({
        ...mem,
        [actionProp]: callActionProp.bind(null, dispatch, actionProps[actionProp]),
    }), {});
};
const createControl = ({ dispatch, entity, instanceId, rfkId, }) => {
    const state = {};
    let config = {};
    let initialized = false;
    const request = getDefaultRequestForWidget(rfkId);
    request.setEntity(entity);
    const query = new Query(instanceId, request);
    const options = mergeDeep(defaultWidgetConfig, WidgetsConfig.get(rfkId, entity)?.options);
    const actions = mapActionsProps(dispatch, options);
    const stateDiff = (state, values) => {
        return Object.fromEntries(Object.entries(values).filter(([k, v]) => state[k] !== v));
    };
    const updateState = (newState) => {
        const updatedState = stateDiff(state, newState);
        Object.assign(state, newState);
        dispatch({
            type: CoreActions.WIDGET_STATE_CHANGED,
            payload: { rfkId, entity, instanceId, state: updatedState },
        });
    };
    const initializeState = () => {
        Object.assign(state, typeof config.state === 'function' ? config.state() : config.state);
    };
    const initializeQuery = () => {
        propStateMapper.mapById(query.getRequest().getRfkId(), query.getRequest(), state);
        if (config.query) {
            config.query(query);
        }
        query.getRequest().takeSnapshot();
    };
    const initialize = (newConfig) => {
        if (!initialized) {
            config = newConfig || {};
            initializeState();
            initializeQuery();
            initialized = true;
        }
    };
    const isInitialized = () => initialized;
    return {
        actions,
        options,
        query,
        state,
        updateState,
        initialize,
        isInitialized,
    };
};
const WidgetContextProvider = ({ children, }) => {
    const { rfkId, entity, dispatch, instanceId } = useBaseWidget();
    const controlRef = useRef();
    if (!controlRef.current) {
        controlRef.current = createControl({ dispatch, entity, instanceId, rfkId });
    }
    useEffect(() => {
        dispatch({
            type: CoreActions.LOAD,
            payload: { rfkId, entity, instanceId },
        });
        return () => {
            dispatch({
                type: CoreActions.UNLOAD,
                payload: { rfkId, entity, instanceId },
            });
        };
    }, [dispatch, rfkId, entity, instanceId]);
    return createElement(WidgetContext.Provider, {
        value: controlRef.current,
    }, children);
};
export default WidgetContextProvider;
